import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { submitAssessment } from '../lib/api';
import { toast } from 'sonner';
import LegalFooter from '../components/LegalFooter';

export default function AssessmentForm() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1 = contact info, 2 = questions
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    business_name: '',
    num_employees: '',
    has_ai_policy: false,
    tracks_ai_tools: false,
    reviews_ai_outputs: false,
    logs_ai_incidents: false,
    employees_acknowledge_rules: false,
    evaluates_vendor_practices: false
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEmployeesChange = (value) => {
    setFormData(prev => ({ ...prev, num_employees: value }));
  };

  const handleQuestionChange = (question, value) => {
    setFormData(prev => ({ ...prev, [question]: value }));
  };

  const validateStep1 = () => {
    if (!formData.name.trim()) {
      toast.error('Please enter your name');
      return false;
    }
    if (!formData.email.trim() || !formData.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return false;
    }
    if (!formData.business_name.trim()) {
      toast.error('Please enter your business name');
      return false;
    }
    if (!formData.num_employees) {
      toast.error('Please select number of employees');
      return false;
    }
    return true;
  };

  const handleNextStep = () => {
    if (validateStep1()) {
      setStep(2);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await submitAssessment(formData);
      const { id, score, status } = response.data;
      
      // Navigate to results page
      navigate(`/assessment/results/${id}`);
    } catch (error) {
      console.error('Assessment submission error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit assessment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const questions = [
    { key: 'has_ai_policy', text: 'Do you have a documented AI usage policy?' },
    { key: 'tracks_ai_tools', text: 'Do you track all AI tools used in your business?' },
    { key: 'reviews_ai_outputs', text: 'Do you review AI-generated outputs before use?' },
    { key: 'logs_ai_incidents', text: 'Do you log incidents or errors related to AI usage?' },
    { key: 'employees_acknowledge_rules', text: 'Do employees acknowledge AI usage rules?' },
    { key: 'evaluates_vendor_practices', text: 'Do you evaluate vendor data practices before using AI tools?' }
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="border-b border-zinc-200">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-xl font-semibold text-zinc-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            AI Accountability Standards Council
          </Link>
          <Link to="/assessment" className="text-sm text-zinc-500 hover:text-zinc-700 flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
        </div>
      </header>

      <main className="flex-1 py-12 px-6">
        <div className="container mx-auto max-w-xl">
          {/* Progress Indicator */}
          <div className="flex items-center justify-center gap-4 mb-10">
            <div className={`flex items-center gap-2 ${step >= 1 ? 'text-zinc-900' : 'text-zinc-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 1 ? 'bg-zinc-900 text-white' : 'bg-zinc-200 text-zinc-500'}`}>
                1
              </div>
              <span className="text-sm font-medium hidden sm:inline">Your Info</span>
            </div>
            <div className="w-12 h-px bg-zinc-300"></div>
            <div className={`flex items-center gap-2 ${step >= 2 ? 'text-zinc-900' : 'text-zinc-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 2 ? 'bg-zinc-900 text-white' : 'bg-zinc-200 text-zinc-500'}`}>
                2
              </div>
              <span className="text-sm font-medium hidden sm:inline">Assessment</span>
            </div>
          </div>

          <div className="bg-white border border-zinc-200 p-8">
            {step === 1 ? (
              <>
                <h1 
                  className="text-2xl font-semibold text-zinc-900 mb-2 text-center"
                  style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                >
                  AI Risk Score Assessment
                </h1>
                <p className="text-zinc-600 text-center mb-8">
                  Enter your information to begin
                </p>

                <div className="space-y-6">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your full name"
                      className="mt-1 rounded-none"
                      data-testid="input-name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="you@company.com"
                      className="mt-1 rounded-none"
                      data-testid="input-email"
                    />
                  </div>

                  <div>
                    <Label htmlFor="business_name">Business Name *</Label>
                    <Input
                      id="business_name"
                      name="business_name"
                      value={formData.business_name}
                      onChange={handleInputChange}
                      placeholder="Your company name"
                      className="mt-1 rounded-none"
                      data-testid="input-business-name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="num_employees">Number of Employees *</Label>
                    <Select value={formData.num_employees} onValueChange={handleEmployeesChange}>
                      <SelectTrigger className="mt-1 rounded-none" data-testid="select-employees">
                        <SelectValue placeholder="Select range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-10">1-10</SelectItem>
                        <SelectItem value="11-50">11-50</SelectItem>
                        <SelectItem value="51-200">51-200</SelectItem>
                        <SelectItem value="201-500">201-500</SelectItem>
                        <SelectItem value="500+">500+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button 
                    onClick={handleNextStep}
                    className="w-full rounded-none bg-zinc-900 hover:bg-zinc-800"
                    data-testid="continue-btn"
                  >
                    Continue to Assessment
                  </Button>
                </div>
              </>
            ) : (
              <form onSubmit={handleSubmit}>
                <h1 
                  className="text-2xl font-semibold text-zinc-900 mb-2 text-center"
                  style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                >
                  Assessment Questions
                </h1>
                <p className="text-zinc-600 text-center mb-8">
                  Answer Yes or No for each question
                </p>

                <div className="space-y-6">
                  {questions.map((q, index) => (
                    <div key={q.key} className="border border-zinc-200 p-4">
                      <p className="text-zinc-900 mb-4">{index + 1}. {q.text}</p>
                      <div className="flex gap-4">
                        <Button
                          type="button"
                          variant={formData[q.key] === true ? "default" : "outline"}
                          onClick={() => handleQuestionChange(q.key, true)}
                          className={`flex-1 rounded-none ${formData[q.key] === true ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                          data-testid={`${q.key}-yes`}
                        >
                          Yes
                        </Button>
                        <Button
                          type="button"
                          variant={formData[q.key] === false ? "default" : "outline"}
                          onClick={() => handleQuestionChange(q.key, false)}
                          className={`flex-1 rounded-none ${formData[q.key] === false ? 'bg-zinc-600 hover:bg-zinc-700' : ''}`}
                          data-testid={`${q.key}-no`}
                        >
                          No
                        </Button>
                      </div>
                    </div>
                  ))}

                  <div className="flex gap-4 pt-4">
                    <Button 
                      type="button"
                      variant="outline"
                      onClick={() => setStep(1)}
                      className="flex-1 rounded-none"
                      data-testid="back-btn"
                    >
                      Back
                    </Button>
                    <Button 
                      type="submit"
                      disabled={loading}
                      className="flex-1 rounded-none bg-zinc-900 hover:bg-zinc-800"
                      data-testid="submit-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Calculating...
                        </>
                      ) : (
                        'Get My Score'
                      )}
                    </Button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </main>

      <LegalFooter />
    </div>
  );
}
